import type { ColDef } from "ag-grid-enterprise";
import { useEffect, useState } from "react";

import ManageRoute from "@web-app/pages/dispatchPage/ManageRoute";
import useDispatchStore from "@web-app/store/dispatchStore";
import { Order, Route } from "@web-app/types";

import { OrderDoordashDetails } from "./ExternalAssignee/Doordash";
import { OrderExpediteDetails } from "./ExternalAssignee/Expedite";
import { OrderOnfleetDetails } from "./ExternalAssignee/Onfleet";
import { OrderRoadieDetails } from "./ExternalAssignee/Roadie";
import { OrderUberDetails } from "./ExternalAssignee/Uber";
import Header from "./Header";
import List from "./List";
import OrderDetailsPanel from "./OrderDetailsPanel";
import OrderEditPanelWrapper from "./OrderEditPanelWrapper";
import OrderReportIssuePanel from "./OrderReportIssuePanel";
import SidebarTable from "./Table";
import useOrderSidebar from "./useOrderSidebar";

export default function OrdersSidebar() {
  const {
    appsMap,
    assigneesMap,
    autoAssignSearchingIds,
    isAppsLoading,
    isAssigneesLoading,
    unroutedOrders,
    routedOrdersMap,
    routes,
    sortParameter,
    setSearchTerm,
    setSortParameter,
    groupParameter,
    setGroupParameter,
    sidebarMode,
    setSidebarMode,
    isLoading,
    searchTerm,
    focusedOrderId,
    focusedRouteId,
    focusedOrder,
    onCopy,
    onShare,
    onDelete,
    columnDefs,
    setColumnDefs,
    isEditOrder,
    reportIssueOrderId,
  } = useOrderSidebar();

  const commonHeaderProps = {
    groupParameter,
    searchTerm,
    setGroupParameter,
    setSearchTerm,
    setSidebarMode,
    setSortParameter,
    sidebarMode,
    sortParameter,
  };

  const setColumnDefsAndPersist = (defs: ColDef[]) => {
    setColumnDefs(defs);
    localStorage.setItem(
      "columnDefsDispatchPage",
      JSON.stringify(
        defs
          .map((col) => {
            const colId = col.colId ?? col.field;
            if (!colId) return null;
            return {
              colId,
              flex: col.flex,
              hide: col.hide,
              pinned: col.pinned,
              sort: col.sort,
              sortIndex: col.sortIndex,
              width: col.width,
            };
          })
          .filter(Boolean),
      ),
    );
  };

  const [selectedProvider, setSelectedProvider] = useState<{
    entity: Order | Route | null;
    orders: Order[];
    provider: string;
    type: string;
  }>({
    entity: null,
    orders: [],
    provider: "",
    type: "",
  });

  const getRouteTimezone = (routeId: string) => {
    const route = routes.find((route) => route.id === routeId);
    const firstOrder = route?.ordersOrder[0];
    const order = routedOrdersMap.get(firstOrder?.id || "");
    return order?.timezone || "system";
  };

  const onSelectExternalProvider = (
    entity: Order | Route | null,
    provider: string,
    type: string,
  ) => {
    const orders: Order[] = [];
    if (entity && type === "order") {
      orders.push(entity as Order);
    } else if (entity && type === "route") {
      const orderIds = (entity as Route).ordersOrder.map((o) => o.id);
      orderIds.forEach((orderId) => {
        const order = routedOrdersMap.get(orderId);
        if (order) {
          orders.push(order);
        }
      });
    }
    setSelectedProvider({ entity, orders, provider, type });
  };

  const isOrderEdit = !!(focusedOrderId && focusedOrder && isEditOrder);
  const isOrderReportIssue = !!(
    focusedOrderId &&
    focusedOrder &&
    reportIssueOrderId === focusedOrderId
  );
  const isOrderView = !!(
    focusedOrderId &&
    focusedOrder &&
    !isEditOrder &&
    !isOrderReportIssue
  );
  const isRoutePageView = !!focusedRouteId;

  useEffect(() => {
    if (isRoutePageView) {
      useDispatchStore.setState({
        canViewOrderCard: false,
        canViewRouteCard: true,
      });
    } else if (isOrderEdit || isOrderReportIssue || isOrderView) {
      useDispatchStore.setState({
        canViewOrderCard: true,
        canViewRouteCard: false,
      });
    } else {
      useDispatchStore.setState({
        canViewOrderCard: false,
        canViewRouteCard: false,
      });
    }
  }, [isOrderEdit, isOrderReportIssue, isOrderView, isRoutePageView]);

  return (
    <div className="relative flex lg:mt-2 h-[calc(100%-40px)] 2xl:h-[calc(100%-8px)] flex-col gap-2 overflow-y-auto orders-sidebar">
      {isOrderEdit && (
        <OrderEditPanelWrapper
          order={focusedOrder}
          onClose={() => {
            useDispatchStore.setState({
              focusedOrderId: null,
              isEditOrder: false,
              mapFocusedOrderId: null,
            });
          }}
        />
      )}
      {isOrderReportIssue && (
        <OrderReportIssuePanel
          order={focusedOrder}
          onClose={() => {
            useDispatchStore.setState({
              focusedOrderId: null,
              mapFocusedOrderId: null,
              reportIssueOrderId: null,
              selectedSidebarOrderIds: [],
              selectedSidebarRouteIds: [],
            });
          }}
        />
      )}
      {selectedProvider.provider === "doordash" && selectedProvider.entity && (
        <OrderDoordashDetails
          order={selectedProvider.entity as Order}
          onClose={() => {
            onSelectExternalProvider(null, "", "");
          }}
        />
      )}
      {selectedProvider.provider === "uber" &&
        selectedProvider.type === "order" && (
          <OrderUberDetails
            entity="order"
            entityId={selectedProvider.entity?.id || ""}
            timezone={(selectedProvider.entity as Order)?.timezone || "system"}
            onClose={() => {
              onSelectExternalProvider(null, "", "");
            }}
            relatedOrders={selectedProvider.orders}
          />
        )}
      {selectedProvider.provider === "uber" &&
        selectedProvider.type === "route" && (
          <OrderUberDetails
            entity="route"
            entityId={selectedProvider.entity?.id || ""}
            timezone={getRouteTimezone(selectedProvider.entity?.id || "")}
            onClose={() => {
              onSelectExternalProvider(null, "", "");
            }}
            relatedOrders={selectedProvider.orders}
          />
        )}
      {selectedProvider.provider === "roadie" &&
        selectedProvider.type === "route" && (
          <OrderRoadieDetails
            entity="route"
            entityId={selectedProvider.entity?.id || ""}
            orders={Array.from(routedOrdersMap.values())
              .map((routedOrder) => {
                if (routedOrder.routeId === selectedProvider.entity?.id) {
                  return routedOrder;
                }
                return null;
              })
              .filter((order) => order !== null)}
            onClose={() => {
              onSelectExternalProvider(null, "", "");
            }}
          />
        )}
      {selectedProvider.provider === "roadie" &&
        selectedProvider.type === "order" && (
          <OrderRoadieDetails
            entity="order"
            entityId={selectedProvider.entity?.id || ""}
            orders={[selectedProvider.entity as Order]}
            onClose={() => {
              onSelectExternalProvider(null, "", "");
            }}
          />
        )}
      {selectedProvider.provider === "expedite" &&
        selectedProvider.type === "order" && (
          <OrderExpediteDetails
            entity="order"
            entityId={selectedProvider.entity?.id || ""}
            onClose={() => {
              onSelectExternalProvider(null, "", "");
            }}
            relatedOrders={selectedProvider.orders}
          />
        )}
      {selectedProvider.provider === "expedite" &&
        selectedProvider.type === "route" && (
          <OrderExpediteDetails
            entity="route"
            entityId={selectedProvider.entity?.id || ""}
            onClose={() => {
              onSelectExternalProvider(null, "", "");
            }}
            relatedOrders={selectedProvider.orders}
          />
        )}
      {selectedProvider.provider === "onfleet" &&
        selectedProvider.type === "order" && (
          <OrderOnfleetDetails
            entity="order"
            entityId={selectedProvider.entity?.id || ""}
            orders={[selectedProvider.entity as Order]}
            onClose={() => {
              onSelectExternalProvider(null, "", "");
            }}
          />
        )}
      {selectedProvider.provider === "onfleet" &&
        selectedProvider.type === "route" && (
          <OrderOnfleetDetails
            entity="route"
            entityId={selectedProvider.entity?.id || ""}
            route={selectedProvider.entity as Route}
            orders={Array.from(routedOrdersMap.values())
              .map((routedOrder) => {
                if (routedOrder.routeId === selectedProvider.entity?.id) {
                  return routedOrder;
                }
                return null;
              })
              .filter((order) => order !== null)}
            onClose={() => {
              onSelectExternalProvider(null, "", "");
            }}
          />
        )}
      {focusedRouteId && <ManageRoute />}
      {isOrderView && (
        <OrderDetailsPanel
          key={focusedOrder.id}
          order={focusedOrder}
          assigneesMap={assigneesMap}
          appsMap={appsMap}
        />
      )}
      {sidebarMode === "list" && (
        <>
          <Header {...commonHeaderProps} />
          <List
            appsMap={appsMap}
            assigneesMap={assigneesMap}
            autoAssignSearchingIds={autoAssignSearchingIds}
            groupParameter={groupParameter}
            isAppsLoading={isAppsLoading}
            isAssigneesLoading={isAssigneesLoading}
            searchTerm={searchTerm}
            isLoading={isLoading}
            unroutedOrders={unroutedOrders}
            routedOrdersMap={routedOrdersMap}
            routes={routes}
            onCopy={onCopy}
            onShare={onShare}
            onDelete={onDelete}
            onSelectExternalProvider={onSelectExternalProvider}
          />
        </>
      )}
      {!focusedOrderId && !focusedRouteId && sidebarMode === "table" && (
        <>
          <Header
            {...commonHeaderProps}
            setColumnDefs={setColumnDefsAndPersist}
            columnDefs={columnDefs}
          />
          <SidebarTable
            assigneesMap={assigneesMap}
            isLoading={isLoading}
            unroutedOrders={unroutedOrders}
            routedOrdersMap={routedOrdersMap}
            routes={routes}
            setColumnDefs={setColumnDefs}
            columnDefs={columnDefs}
          />
        </>
      )}
    </div>
  );
}
